/**
 * Shared utility functions for filtering employees
 * Used by both TimeTracking.js and timeRecordService.js
 */

import { toYMDString, todayManilaYMD } from './periodDate';

/**
 * Was ONE employee employed on a given calendar date?
 *
 * The single definition of "employed on this date". `filterEmployeesByDate` is this
 * applied to a list, and `isEmployeeActiveToday` is this applied to today — so the
 * Employee Management list, Time Tracking and the DTR picker can't drift apart.
 *
 * Compare on 'YYYY-MM-DD' calendar-date strings, never raw Date instants. A startDate
 * stored as a UTC-midnight Timestamp resolves to a non-midnight local instant, so
 * `startDate <= date` (both Dates) would wrongly exclude an employee whose first day IS
 * the selected date — throwing "employee no longer available" when recording a same-day
 * DTR. toYMDString absorbs Timestamp | Date | string and returns '' for missing dates
 * (→ no start/end constraint).
 *
 * Inclusive of BOTH boundaries: employed from startDate, and THROUGH endDate (the last
 * working day). Matches the kiosk (`employeeLookup.isEmployeeActiveOn`) and billing
 * (`isActiveForBilling`) so the app, the clock and the invoice agree.
 *
 * @param {Object} employee - employee record (reads employment.startDate/endDate)
 * @param {Date|string} date - the calendar date to test
 * @returns {boolean} true when the date falls inside the employment window
 */
export const isEmployeeActiveOnDate = (employee, date) => {
  const dateYMD = toYMDString(date);
  const startYMD = toYMDString(employee?.employment?.startDate);
  const endYMD = toYMDString(employee?.employment?.endDate);

  const isStartValid = !startYMD || startYMD <= dateYMD;
  const isEndValid = !endYMD || endYMD >= dateYMD;

  // Note: NOT using emp.status field — employment dates are the source of truth.
  return isStartValid && isEndValid;
};

/**
 * Is this employee employed TODAY (Asia/Manila)?
 *
 * The display predicate for every Active/Inactive badge, count and filter in the app.
 * Employment dates are the ONLY source of truth: the stored `status` block is neither
 * read nor written anywhere, and the nightly batch that maintained it is gone
 * (PRD_EmploymentDatesSingleSource C1a/M1). A cleared end date reads Active at once.
 *
 * Manila, not the browser's clock: Sahod is Philippines-only and the operator may sit
 * in another timezone, so `new Date()` could resolve the wrong calendar day near
 * midnight.
 *
 * @param {Object} employee - employee record
 * @returns {boolean} true when employed today
 */
export const isEmployeeActiveToday = (employee) =>
  isEmployeeActiveOnDate(employee, todayManilaYMD());

/**
 * Filter employees by date based on employment dates
 * @param {Array} employees - All employees
 * @param {Date} date - Selected date to filter by
 * @returns {Array} - Filtered employees active on the date
 */
export const filterEmployeesByDate = (employees, date) =>
  employees.filter(emp => isEmployeeActiveOnDate(emp, date));

